// src/store/modules/phone.js
import sipService from '@/services/phone/SipService'
import SoftphoneService from '@/services/phone/SoftphoneService'
import presenceClient from '@/services/presence/PresenceClient'

const state = () => ({
  ui: {
    visible: false,
    tab: 'dialer',
    showDevices: false,
    presence: 'Available',

    localPresenceAvailable: false,
    localPresenceEnabled: false,

    displayName: 'User',
    extension: '—',

    wlb: {
      active: false,
      mode: null,
      targetUserId: '',
      targetSipUserId: '',
      targetName: '',
      targetExt: '',
      customerId: ''
    }
  },

  ua: {
    started: false,
    transportState: 'Disconnected',
    regState: 'Unregistered',
    lastError: null,
    sipUsername: null,
    sipDomain: null
  },

  call: {
    direction: null,
    state: 'idle',
    from: '',
    fromName: '',
    to: '',
    muted: false,
    onHold: false,
    durationMs: 0
  },

  devices: {
    inputs: [],
    outputs: [],
    selectedInputId: null,
    selectedOutputId: null
  },

  recents: []
})

let durationTimer = null
let wired = false
let bootstrapPromise = null

function errorMsg(e, fallback = 'Something went wrong') {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    (typeof e === 'string' ? e : null) ||
    fallback
  )
}

function normalizeTransportState(value) {
  const v = String(value || '').toLowerCase()
  if (v === 'connected') return 'Connected'
  if (v === 'connecting') return 'Connecting'
  if (v === 'disconnected') return 'Disconnected'
  return 'Disconnected'
}

function normalizeRegState(value) {
  const v = String(value || '').toLowerCase()
  if (v === 'registered') return 'Registered'
  if (v === 'registering') return 'Registering'
  if (v === 'unregistered') return 'Unregistered'
  if (v === 'failed') return 'Failed'
  return 'Unregistered'
}

function isBenignCallError(value) {
  const v = String(value || '').trim().toLowerCase()
  return [
    'canceled',
    'cancelled',
    'rejected',
    'busy',
    'busy here',
    'bye',
    'terminated',
    'ended',
    'request terminated'
  ].includes(v)
}

function mapCallState(rawState, callDirection, currentState) {
  const v = String(rawState || '').toLowerCase()
  const direction = String(callDirection || '').toLowerCase()

  if (v === 'terminated' || v === 'terminating') return 'idle'
  if (v === 'established') return 'active'

  if (direction === 'incoming') {
    if (currentState === 'ringing') {
      if (v === 'connecting' || v === 'progress' || v === 'accepted' || v === 'initial') {
        return 'ringing'
      }
    }

    if (v === 'connecting' || v === 'progress' || v === 'accepted' || v === 'initial') {
      return 'connecting'
    }
  }

  if (v === 'initial') return 'connecting'
  if (v === 'connecting') return 'connecting'
  if (v === 'progress') return 'connecting'
  if (v === 'accepted') return 'connecting'

  return null
}

function isWlbDialString(s) {
  const v = String(s || '').trim()
  return v.startsWith('*90') || v.startsWith('*91') || v.startsWith('*92')
}

function normalizeWlbDialString(s) {
  return String(s || '').trim().replace(/\s+/g, '')
}

function buildWlbHeaders({ customerId, targetUserId, targetSipUserId, mode }) {
  const headers = []

  if (customerId !== undefined && customerId !== null && String(customerId).trim()) {
    headers.push(`X-WLB-CustomerID: ${String(customerId).trim()}`)
  }

  if (targetUserId !== undefined && targetUserId !== null && String(targetUserId).trim()) {
    headers.push(`X-WLB-Target-UserID: ${String(targetUserId).trim()}`)
  }

  if (targetSipUserId !== undefined && targetSipUserId !== null && String(targetSipUserId).trim()) {
    headers.push(`X-WLB-Target-SIPUserID: ${String(targetSipUserId).trim()}`)
  }

  if (mode && String(mode).trim()) {
    headers.push(`X-WLB-Mode: ${String(mode).trim()}`)
  }

  return headers
}

async function showIncomingCallNotification({ from, name }) {
  try {
    if (!('Notification' in window)) return
    if (!('serviceWorker' in navigator)) return

    if (Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') return
    }

    const reg = await navigator.serviceWorker.ready

    const callerLabel = (name && String(name).trim())
      ? String(name).trim()
      : (from ? String(from) : 'Unknown caller')

    await reg.showNotification('Incoming call', {
      body: callerLabel,
      icon: '/assets/logo.png',
      badge: '/assets/logo.png',
      tag: 'incoming-call',
      renotify: true,
      requireInteraction: true,
      data: { from, name, ts: Date.now() },
      actions: [
        { action: 'answer', title: 'Answer' },
        { action: 'decline', title: 'Decline' }
      ]
    })
  } catch (e) {}
}

function applyLpPrefix(target, lpEnabled) {
  let s = String(target || '').trim()
  if (!s) return s

  if (lpEnabled) {
    s = s.replace(/[^\d*#]/g, '')
    const digits = s.replace(/\D/g, '')
    const isExternal = digits.length >= 10
    if (isExternal && !s.startsWith('0')) s = `0${s}`
    return s
  }

  return s.replace(/[^\d+*#]/g, '')
}

const mutations = {
  SET_UI(state, patch) {
    const next = { ...state.ui, ...patch }
    if (patch && Object.prototype.hasOwnProperty.call(patch, 'wlb')) {
      next.wlb = { ...state.ui.wlb, ...(patch.wlb || {}) }
    }
    state.ui = next
  },

  SET_UA(state, patch) {
    state.ua = { ...state.ua, ...patch }
  },

  SET_CALL(state, patch) {
    state.call = { ...state.call, ...patch }
  },

  SET_DEVICES(state, patch) {
    state.devices = { ...state.devices, ...patch }
  },

  PUSH_RECENT(state, item) {
    state.recents.unshift(item)
    state.recents = state.recents.slice(0, 30)
  },

  RESET_CALL(state) {
    state.call = {
      direction: null,
      state: 'idle',
      from: '',
      fromName: '',
      to: '',
      muted: false,
      onHold: false,
      durationMs: 0
    }
  }
}

const actions = {
  async bootstrap({ commit, dispatch, state }) {
    if (state.ua.started) return
    if (bootstrapPromise) return bootstrapPromise

    commit('SET_UA', { lastError: null })

    bootstrapPromise = (async () => {
      if (!wired) {
        wired = true

        sipService.on('ua:started', () =>
          commit('SET_UA', { started: true, lastError: null })
        )

        sipService.on('ua:stopped', () =>
          commit('SET_UA', {
            started: false,
            transportState: 'Disconnected',
            regState: 'Unregistered'
          })
        )

        sipService.on('transport:state', (s) => {
          const next = normalizeTransportState(s)
          commit('SET_UA', {
            transportState: next,
            lastError: next === 'Connected' ? null : state.ua.lastError
          })
        })

        sipService.on('reg:state', (s) => {
          const next = normalizeRegState(s)
          commit('SET_UA', {
            regState: next,
            lastError: next === 'Registered' ? null : state.ua.lastError
          })
        })

        sipService.on('reg:ok', () => {
          commit('SET_UA', { lastError: null })
        })

        sipService.on('reg:error', (e) => {
          commit('SET_UA', { lastError: errorMsg(e) })
        })

        sipService.on('call:error', (e) => {
          const msg = errorMsg(e)
          if (isBenignCallError(msg)) return
          commit('SET_UA', { lastError: msg })
        })

        sipService.on('call:incoming', ({ from, name }) => {
          commit('SET_UI', { wlb: { active: false, mode: null } })

          commit('SET_CALL', {
            direction: 'incoming',
            state: 'ringing',
            from,
            fromName: name || '',
            to: '',
            muted: false,
            onHold: false,
            durationMs: 0
          })

          commit('SET_UI', { visible: true, tab: 'dialer' })
          showIncomingCallNotification({ from, name })
        })

        sipService.on('call:outgoing', ({ to }) => {
          commit('SET_CALL', {
            direction: 'outgoing',
            state: 'dialing',
            to,
            from: '',
            fromName: '',
            muted: false,
            onHold: false,
            durationMs: 0
          })

          commit('SET_UI', { visible: true, tab: 'dialer' })
        })

        sipService.on('call:state', (s) => {
          const next = mapCallState(s, state.call.direction, state.call.state)
          if (next) {
            commit('SET_CALL', { state: next })
          }

          const stateName = String(s || '').toLowerCase()

          if (stateName === 'established') {
            commit('SET_UA', { lastError: null })
            dispatch('startDurationTimer')
          }

          if (stateName === 'terminating' || stateName === 'terminated') {
            dispatch('endCall')
          }
        })

        sipService.on('call:mute', (muted) => commit('SET_CALL', { muted: !!muted }))
        sipService.on('call:hold', (hold) => commit('SET_CALL', { onHold: !!hold }))

        sipService.on('devices:changed', ({ inputId, outputId }) => {
          commit('SET_DEVICES', { selectedInputId: inputId, selectedOutputId: outputId })
        })

        sipService.on('call:ended', () => dispatch('endCall'))

        presenceClient.on('msg', async (m) => {
          if (!m?.topic) return

          if (m.topic === 'click2dial.dial') {
            const number = String(m?.payload?.number || '').trim()
            if (!number) return
            if (['ringing', 'dialing', 'connecting', 'active'].includes(state.call.state)) return

            commit('SET_UI', { visible: true, tab: 'dialer' })

            try {
              await dispatch('bootstrap')
              await dispatch('dial', number)
            } catch (e) {}

            return
          }

          if (m.topic === 'softphone:hangup') {
            if (!['ringing', 'dialing', 'connecting', 'active'].includes(state.call.state)) return
            try {
              await dispatch('hangup')
            } catch (e) {}
          }

          if (m.topic === 'video:invite') {
            dispatch('meetings/setIncomingInvite', m.payload, { root: true })
          }
        })
      }

      let creds = null
      try {
        const res = await SoftphoneService.mySoftphone()
        creds = res?.data || res || null
      } catch (e) {
        commit('SET_UA', { lastError: errorMsg(e, 'Failed to load softphone credentials') })
        throw e
      }

      const config = {
        wss: creds?.wss || process.env.VUE_APP_SIP_WSS || 'wss://everest.talkvox.com/ws',
        domain: creds?.domain || process.env.VUE_APP_SIP_DOMAIN || 'everest.talkvox.com',
        username: creds?.username || process.env.VUE_APP_SIP_USERNAME,
        password: creds?.password || process.env.VUE_APP_SIP_PASSWORD,
        displayName: creds?.displayName || creds?.name || process.env.VUE_APP_SIP_DISPLAY_NAME || 'Soft Phone'
      }

      if (!config.username || !config.password) {
        const msg = 'Softphone credentials missing username/password'
        commit('SET_UA', { lastError: msg })
        throw new Error(msg)
      }

      const lpAvailable = creds?.localPresence === true
      const lpEnabled = lpAvailable ? true : false

      commit('SET_UI', {
        displayName: String(creds?.name || creds?.displayName || config.displayName || 'User'),
        extension: String(creds?.extension || '—'),
        localPresenceAvailable: lpAvailable,
        localPresenceEnabled: lpEnabled
      })

      commit('SET_UA', {
        sipUsername: config.username,
        sipDomain: config.domain,
        lastError: null
      })

      await sipService.init(config)
      await sipService.register()
      await dispatch('refreshDevices')
    })().finally(() => {
      bootstrapPromise = null
    })

    return bootstrapPromise
  },

  async shutdown({ dispatch, commit }) {
    dispatch('stopDurationTimer')
    commit('RESET_CALL')
    commit('SET_UI', { wlb: { active: false, mode: null } })
    await sipService.destroy()
  },

  clearLastError({ commit }) {
    commit('SET_UA', { lastError: null })
  },

  showPhone({ commit }) {
    commit('SET_UI', { visible: true })
  },

  hidePhone({ commit }) {
    commit('SET_UI', { visible: false })
  },

  togglePhone({ commit, state }) {
    commit('SET_UI', { visible: !state.ui.visible })
  },

  setPresence({ commit }, presence) {
    commit('SET_UI', { presence })
  },

  setTab({ commit }, tab) {
    commit('SET_UI', { tab })
  },

  openDevices({ commit }) {
    commit('SET_UI', { showDevices: true })
  },

  closeDevices({ commit }) {
    commit('SET_UI', { showDevices: false })
  },

  async setLocalPresenceEnabled({ commit, state }, enabled) {
    if (!state.ui.localPresenceAvailable) return

    commit('SET_UI', { localPresenceEnabled: !!enabled })

    try {
      if (typeof SoftphoneService?.setLocalPresence === 'function') {
        await SoftphoneService.setLocalPresence(!!enabled)
      } else if (typeof SoftphoneService?.updateLocalPresence === 'function') {
        await SoftphoneService.updateLocalPresence(!!enabled)
      }
    } catch (e) {}
  },

  async dial({ commit, state }, number) {
    commit('SET_UA', { lastError: null })
    commit('SET_UI', { wlb: { active: false, mode: null } })

    try {
      const raw = String(number || '').trim()

      if (isWlbDialString(raw)) {
        const target = normalizeWlbDialString(raw)
        await sipService.call(target)
        return
      }

      const target = applyLpPrefix(raw, state.ui.localPresenceEnabled === true)
      await sipService.call(target)
    } catch (e) {
      commit('SET_UA', { lastError: errorMsg(e, 'Failed to place call') })
      throw e
    }
  },

  setWlbUi({ commit }, patch) {
    commit('SET_UI', { wlb: { ...(patch || {}) } })
  },

  clearWlbUi({ commit }) {
    commit('SET_UI', {
      wlb: {
        active: false,
        mode: null,
        targetUserId: '',
        targetSipUserId: '',
        targetName: '',
        targetExt: '',
        customerId: ''
      }
    })
  },

  async wlb({ commit, dispatch, state }, payload) {
    const mode = String(payload?.mode || '').trim()
    const customerId = String(payload?.customerId || '').trim()
    const targetSipUserId = String(payload?.targetSipUserId || '').trim()
    const targetUserId = String(payload?.targetUserId || '').trim()
    const ext = String(payload?.extension || '').trim()
    const targetName = String(payload?.targetName || '').trim()

    if (!mode) return
    if (!customerId) return
    if (!targetSipUserId) return

    if (['ringing', 'dialing', 'connecting', 'active'].includes(state.call.state)) {
      try {
        await dispatch('hangup')
      } catch (e) {}
    }

    commit('SET_UA', { lastError: null })

    commit('SET_UI', {
      visible: true,
      tab: 'dialer',
      wlb: {
        active: true,
        mode,
        customerId,
        targetUserId,
        targetSipUserId,
        targetName,
        targetExt: ext
      }
    })

    try {
      await dispatch('bootstrap')
    } catch (e) {
      commit('SET_UI', { wlb: { active: false, mode: null } })
      return
    }

    await sipService.unlockAudio().catch(() => {})

    const code = mode === 'listen' ? '*90' : mode === 'whisper' ? '*91' : '*92'
    const dialStr = normalizeWlbDialString(code)

    const extraHeaders = buildWlbHeaders({
      customerId,
      targetUserId,
      targetSipUserId,
      mode
    })

    if (ext) extraHeaders.push(`X-WLB-Target-Ext: ${ext}`)

    await sipService.call(dialStr, { extraHeaders })
  },

  wlbListen({ dispatch }, payload) {
    return dispatch('wlb', { ...(payload || {}), mode: 'listen' })
  },

  wlbWhisper({ dispatch }, payload) {
    return dispatch('wlb', { ...(payload || {}), mode: 'whisper' })
  },

  wlbBarge({ dispatch }, payload) {
    return dispatch('wlb', { ...(payload || {}), mode: 'barge' })
  },

  async answer({ commit }) {
    commit('SET_UA', { lastError: null })
    await sipService.unlockAudio().catch(() => {})
    await sipService.answer()
  },

  async hangup({ commit }) {
    await sipService.hangup()
    commit('SET_UI', { wlb: { active: false, mode: null } })
  },

  toggleMute({ commit, state }) {
    const next = !state.call.muted
    sipService.setMute(next)
    commit('SET_CALL', { muted: next })
  },

  async toggleHold({ commit, state }) {
    const next = !state.call.onHold
    await sipService.unlockAudio().catch(() => {})
    await sipService.setHold(next)
    commit('SET_CALL', { onHold: next })
  },

  sendDTMF(_, tone) {
    sipService.unlockAudio().catch(() => {})
    sipService.sendDTMF(tone)
  },

  async transfer({ commit, state }, targetNumber) {
    commit('SET_UA', { lastError: null })
    const target = applyLpPrefix(targetNumber, state.ui.localPresenceEnabled === true)
    await sipService.transfer(target)
  },

  async refreshDevices({ commit }) {
    const { inputs, outputs } = await sipService.listAudioDevices()
    commit('SET_DEVICES', { inputs, outputs })
  },

  async setDevices({ dispatch }, { inputId, outputId }) {
    await sipService.setAudioDevices({ inputId, outputId })
    await dispatch('refreshDevices')
  },

  startDurationTimer({ commit }) {
    if (durationTimer) return
    durationTimer = setInterval(() => {
      commit('SET_CALL', { durationMs: sipService.getCallDurationMs() })
    }, 1000)
  },

  stopDurationTimer({ commit }) {
    if (!durationTimer) return
    clearInterval(durationTimer)
    durationTimer = null
    commit('SET_CALL', { durationMs: 0 })
  },

  endCall({ dispatch, commit, state }) {
    dispatch('stopDurationTimer')

    const c = state.call
    const peer = c.direction === 'incoming' ? c.from : c.to
    if (peer) {
      commit('PUSH_RECENT', {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        peer,
        name: c.fromName || '',
        direction: c.direction,
        when: Date.now(),
        durationMs: c.durationMs || 0
      })
    }

    commit('RESET_CALL')
    commit('SET_UI', { wlb: { active: false, mode: null } })
  }
}

const getters = {
  isConnected: (s) => s.ua.transportState === 'Connected',
  isRegistered: (s) => s.ua.regState === 'Registered',
  canDial: (s, g) => g.isConnected && g.isRegistered,

  statusLabel: (s, g) => {
    if (!navigator.onLine) return 'Offline'
    if (s.ua.transportState === 'Connecting') return 'Connecting…'
    if (!g.isConnected) return 'Offline'
    if (s.ua.regState === 'Registered') return 'Online'
    if (s.ua.regState === 'Registering') return 'Connecting…'
    return 'Not Registered'
  },

  statusTone: (s, g) => {
    const label = getters.statusLabel(s, g)
    if (label === 'Online') return 'good'
    if (label === 'Connecting…') return 'warn'
    return 'bad'
  },

  inCall: (s) => ['ringing', 'dialing', 'connecting', 'active'].includes(s.call.state),
  isActiveCall: (s) => s.call.state === 'active',
  isIncomingRinging: (s) => s.call.state === 'ringing' && s.call.direction === 'incoming',

  callButtonEnabled: (s, g) => g.canDial && !g.inCall,
  canTransfer: (s, g) => g.isActiveCall,

  profileName: (s) => s.ui?.displayName || 'User',
  profileExtension: (s) => s.ui?.extension || '—',

  isWlb: (s) => s.ui?.wlb?.active === true,
  wlbMode: (s) => s.ui?.wlb?.mode || null,
  wlbTargetLabel: (s) => {
    const name = String(s.ui?.wlb?.targetName || '').trim()
    const ext = String(s.ui?.wlb?.targetExt || '').trim()
    const sip = String(s.ui?.wlb?.targetSipUserId || '').trim()
    if (name && ext) return `${name} • Ext ${ext}`
    if (name) return name
    if (ext) return `Ext ${ext}`
    return sip || ''
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}